import React, { useState, useEffect } from 'react';
import contentService from '../services/contentService';
import usuariosService from '../services/usuariosService';
import {
  clienteService,
  proyectoService,
  presupuestoVentaService,
  itemPresupuestoVentaService,
  presupuestoCostoService,
  itemPresupuestoCostoService
} from '../services/onebaseService';
import './PostModal.css';
import RichTextEditor from './RichTextEditor';
import { materialesParaPlataforma, materialPermitido } from '../utils/materialesPlataforma';

// Abre el calendario nativo al hacer clic en cualquier parte del campo de fecha
// (por defecto solo se abre con el ícono). showPicker() requiere un gesto del usuario.
const abrirCalendario = (e) => {
  try {
    e.currentTarget.showPicker?.();
  } catch (_) {
    // navegadores sin soporte: queda el comportamiento nativo (ícono)
  }
};

// 🆕 Helper para obtener icono de plataforma
const getPlatformIcon = (platformName) => {
  const name = platformName?.toLowerCase() || '';
  if (name.includes('facebook')) return 'https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/logos/faccebook.webp';
  if (name.includes('instagram')) return 'https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/logos/instagram.webp';
  if (name.includes('linkedin')) return 'https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/logos/linkedin.png';
  if (name.includes('tiktok')) return 'https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/logos/logo-tiktok-3.avif';
  if (name.includes('twitter') || name.includes('x')) return 'https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/logos/twitter%20x.png';
  if (name.includes('pinterest')) return 'https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/logos/pinterest.jpg';
  if (name.includes('youtube')) return '▶️';
  if (name.includes('whatsapp')) return '💬';
  if (name.includes('telegram')) return '✈️';
  return '🌐';
};

const PostModal = ({ post, onClose, onUpdate }) => {
  const [presupuestoEnabled, setPresupuestoEnabled] = useState(false); // 🆕 Estado del checkbox Presupuesto
  const [formData, setFormData] = useState({
    title: '',
    platform: '',
    material_type: '',
    influencer: '',
    desarrolla: '', // ✅ Campo desarrolla agregado
    cliente: '', // ✅ Campo cliente agregado
    proyecto: '', // 🆕 Campo proyecto agregado
    presupuesto_costo: '', // 🆕 Campo presupuesto costo agregado
    partida_presupuesto: '', // 🆕 Campo partida presupuesto agregado
    item_presupuesto: '', // 🆕 Campo item presupuesto agregado
    cantidad_ejecutada: '1', // 🆕 Campo cantidad ejecutada agregado - default = 1
    publication_date: '',
    publication_time: '', // ✅ Campo publication_time agregado
    validation_date: '',
    brief: '',
    brief_objectives: '',
    brief_target_audience: '',
    brief_key_messages: '',
    etiqueta: '',
    etiqueta_color: '#3B82F6'
  });

  const [platforms, setPlatforms] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [desarrollaOptions, setDesarrollaOptions] = useState([]); // ✅ Estado para desarrolla
  const [clientes, setClientes] = useState([]); // ✅ Estado para clientes
  const [proyectos, setProyectos] = useState([]); // 🆕 Estado para proyectos
  const [proyectosFiltrados, setProyectosFiltrados] = useState([]); // 🆕 Estado para proyectos filtrados
  const [presupuestosCosto, setPresupuestosCosto] = useState([]); // 🆕 Estado para presupuestos de costo
  const [presupuestosCostoFiltrados, setPresupuestosCostoFiltrados] = useState([]); // 🆕 Estado para presupuestos de costo filtrados
  const [partidas, setPartidas] = useState([]); // 🆕 Estado para partidas disponibles
  const [partidasFiltradas, setPartidasFiltradas] = useState([]); // 🆕 Estado para partidas filtradas
  const [itemsPresupuesto, setItemsPresupuesto] = useState([]); // 🆕 Estado para items de presupuesto disponibles
  const [itemsPresupuestoSeleccionados, setItemsPresupuestoSeleccionados] = useState([]); // 🆕 Array de items seleccionados
  const [loadingItems, setLoadingItems] = useState(false); // 🆕 Estado de carga de items
  const [loadingPartidas, setLoadingPartidas] = useState(false); // 🆕 Estado de carga de partidas
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [etiquetasDisponibles, setEtiquetasDisponibles] = useState([]);
  const [etiquetasColores, setEtiquetasColores] = useState({});
  const [etiquetaSelectValue, setEtiquetaSelectValue] = useState(''); // '' | etiqueta | '__nueva__'

  useEffect(() => {
    loadOptions();
    if (post?.publication_date) {
      setFormData(prev => ({
        ...prev,
        publication_date: post.publication_date
      }));
    }
  }, [post]);

  // 🆕 Filtrar proyectos cuando cambia el cliente seleccionado
  useEffect(() => {
    if (formData.cliente) {
      // Filtrar proyectos por cliente
      const proyectosPorCliente = proyectos.filter(p => p.cliente === parseInt(formData.cliente));
      setProyectosFiltrados(proyectosPorCliente);

      // Si el proyecto seleccionado no pertenece al cliente, limpiarlo
      if (formData.proyecto) {
        const proyectoActual = proyectos.find(p => p.id === parseInt(formData.proyecto));
        if (proyectoActual && proyectoActual.cliente !== parseInt(formData.cliente)) {
          setFormData(prev => ({ ...prev, proyecto: '' }));
        }
      }
    } else {
      // Si no hay cliente seleccionado, mostrar todos los proyectos
      setProyectosFiltrados(proyectos);
    }
  }, [formData.cliente, proyectos, formData.proyecto]);

  // 🆕 Filtrar presupuestos de costo cuando cambia el proyecto seleccionado
  useEffect(() => {
    if (formData.proyecto) {
      // Filtrar presupuestos de costo por proyecto
      const presupuestosPorProyecto = presupuestosCosto.filter(p => p.proyecto === parseInt(formData.proyecto));
      setPresupuestosCostoFiltrados(presupuestosPorProyecto);

      // Si el presupuesto de costo seleccionado no pertenece al proyecto, limpiarlo
      if (formData.presupuesto_costo) {
        const presupuestoActual = presupuestosCosto.find(p => p.id === parseInt(formData.presupuesto_costo));
        if (presupuestoActual && presupuestoActual.proyecto !== parseInt(formData.proyecto)) {
          setFormData(prev => ({ ...prev, presupuesto_costo: '' }));
        }
      }
    } else {
      // Si no hay proyecto seleccionado, mostrar todos los presupuestos de costo
      setPresupuestosCostoFiltrados(presupuestosCosto);
    }
  }, [formData.proyecto, presupuestosCosto, formData.presupuesto_costo]);

  // 🆕 Cargar items del presupuesto cuando se selecciona un presupuesto de costo
  useEffect(() => {
    const cargarItemsPresupuesto = async () => {
      if (formData.presupuesto_costo) {
        setLoadingItems(true);
        try {
          // Obtener items del presupuesto de costo seleccionado
          const itemsRes = await itemPresupuestoCostoService.getAll({
            presupuesto_costo: formData.presupuesto_costo
          });

          const items = itemsRes.data.results || itemsRes.data || [];

          // Calcular cantidades disponibles para cada item
          const itemsConDisponibilidad = items.map(item => {
            const cantidadTotal = parseFloat(item.cantidad) || 0;
            const cantidadEjecutada = parseFloat(item.cantidad_ejecutada) || 0;
            const cantidadDisponible = cantidadTotal - cantidadEjecutada;

            return {
              ...item,
              cantidad_disponible: Math.max(0, cantidadDisponible)
            };
          });

          setItemsPresupuesto(itemsConDisponibilidad);
        } catch (error) {
          console.error('Error cargando items de presupuesto:', error);
          setItemsPresupuesto([]);
        } finally {
          setLoadingItems(false);
        }
      } else {
        // Limpiar items si no hay presupuesto de costo seleccionado
        setItemsPresupuesto([]);
        setFormData(prev => ({
          ...prev,
          item_presupuesto: '',
          cantidad_ejecutada: ''
        }));
      }
    };

    cargarItemsPresupuesto();
  }, [formData.presupuesto_costo]);

  // 🆕 Cargar partidas del presupuesto cuando se selecciona un presupuesto de costo
  useEffect(() => {
    const cargarPartidas = async () => {
      if (formData.presupuesto_costo) {
        setLoadingPartidas(true);
        try {
          console.log('📋 Cargando presupuesto ID:', formData.presupuesto_costo);

          // Obtener el presupuesto de costo con sus áreas y partidas
          const presupuestoRes = await presupuestoCostoService.getById(formData.presupuesto_costo);
          const presupuesto = presupuestoRes.data;

          console.log('📋 Presupuesto obtenido:', presupuesto);
          console.log('📋 Áreas disponibles:', presupuesto.areas_asignadas || presupuesto.areas);

          // Extraer todas las partidas del presupuesto
          let allPartidas = [];

          // Intentar acceder a áreas_asignadas primero (nombre correcto en el modelo)
          const areas = presupuesto.areas_asignadas || presupuesto.areas || [];

          if (Array.isArray(areas)) {
            areas.forEach(area => {
              console.log('🔍 Procesando área:', area.nombre, '- Partidas:', area.partidas_estructura);
              // Intentar acceder a partidas_estructura (nombre correcto en el modelo)
              const partidas = area.partidas_estructura || area.partidas || [];
              if (Array.isArray(partidas)) {
                allPartidas = allPartidas.concat(partidas);
              }
            });
          }

          console.log('✅ Total partidas cargadas:', allPartidas.length, allPartidas);
          setPartidas(allPartidas);
          setPartidasFiltradas(allPartidas);
        } catch (error) {
          console.error('❌ Error cargando partidas del presupuesto:', error);
          setPartidas([]);
          setPartidasFiltradas([]);
        } finally {
          setLoadingPartidas(false);
        }
      } else {
        // Limpiar partidas si no hay presupuesto de costo seleccionado
        setPartidas([]);
        setPartidasFiltradas([]);
        setFormData(prev => ({
          ...prev,
          partida_presupuesto: '',
          cantidad_ejecutada: '1'
        }));
      }
    };

    cargarPartidas();
  }, [formData.presupuesto_costo]);

  const loadOptions = async () => {
    try {
      console.log('🔄 Iniciando carga de opciones...');

      const [platformsRes, influencersRes, desarrollaRes, clientesRes, proyectosRes, presupuestosCostoRes, etiquetasRes] = await Promise.all([
        contentService.getPlatforms().catch(err => {
          console.error('❌ Error cargando platforms:', err);
          return { data: [] };
        }),
        contentService.getInfluencers().catch(err => {
          console.error('❌ Error cargando influencers:', err);
          return { data: [] };
        }),
        contentService.getDesarrolla().catch(err => {
          console.error('❌ Error cargando desarrolla:', err);
          return { data: [] };
        }),
        clienteService.getAll().catch(err => {
          console.error('❌ Error cargando clientes:', err);
          return { data: [] };
        }),
        proyectoService.getAll().catch(err => {
          console.error('❌ Error cargando proyectos:', err);
          return { data: [] };
        }),
        presupuestoCostoService.getAll().catch(err => {
          console.error('❌ Error cargando presupuestos costo:', err);
          return { data: [] };
        }),
        usuariosService.getEtiquetasDisponibles().catch(() => ({ etiquetas: [] }))
      ]);

      console.log('📦 Respuestas recibidas:', {
        platforms: platformsRes,
        influencers: influencersRes,
        desarrolla: desarrollaRes,
        clientes: clientesRes,
        proyectos: proyectosRes,
        presupuestosCosto: presupuestosCostoRes
      });

      const platformsData = platformsRes.data.results || platformsRes.data || [];
      const influencersData = influencersRes.data.results || influencersRes.data || [];
      const desarrollaData = desarrollaRes.data.results || desarrollaRes.data || [];
      const clientesData = clientesRes.data.results || clientesRes.data || [];
      const proyectosData = proyectosRes.data.results || proyectosRes.data || [];
      const presupuestosCostoData = presupuestosCostoRes.data.results || presupuestosCostoRes.data || [];

      console.log('✅ Datos procesados:', {
        platforms: platformsData.length,
        influencers: influencersData.length,
        desarrolla: desarrollaData.length,
        clientes: clientesData.length,
        proyectos: proyectosData.length,
        presupuestosCosto: presupuestosCostoData.length
      });

      setPlatforms(Array.isArray(platformsData) ? platformsData : []);
      setInfluencers(Array.isArray(influencersData) ? influencersData : []);
      setDesarrollaOptions(Array.isArray(desarrollaData) ? desarrollaData : []);
      setClientes(Array.isArray(clientesData) ? clientesData : []);
      setProyectos(Array.isArray(proyectosData) ? proyectosData : []);
      setPresupuestosCosto(Array.isArray(presupuestosCostoData) ? presupuestosCostoData : []);
      setEtiquetasDisponibles(Array.isArray(etiquetasRes?.etiquetas) ? etiquetasRes.etiquetas : []);
      setEtiquetasColores(etiquetasRes?.etiquetas_colores || {});

      console.log('✅ Estados actualizados correctamente');
    } catch (error) {
      console.error('❌ Error loading options:', error);
      console.error('❌ Error details:', error.response?.data);
      setPlatforms([]);
      setInfluencers([]);
      setDesarrollaOptions([]);
      setClientes([]);
      setProyectos([]);
      setPresupuestosCosto([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'El título es requerido';
    if (!formData.desarrolla) newErrors.desarrolla = 'El tipo de desarrollo es requerido';
    if (!formData.platform) newErrors.platform = 'La plataforma es requerida';
    if (!formData.material_type) newErrors.material_type = 'El tipo de material es requerido';
    if (!formData.influencer) newErrors.influencer = 'El influencer es requerido';
    if (!formData.publication_date) newErrors.publication_date = 'La fecha de publicación es requerida';
    if (!formData.validation_date) newErrors.validation_date = 'La fecha de validación es requerida';
    const briefText = formData.brief.replace(/<[^>]*>/g, '').trim();
    if (!briefText) newErrors.brief = 'El brief es requerido';

    // 🆕 Validar campos de presupuesto solo si el checkbox está activo
    if (presupuestoEnabled) {
      if (!formData.cliente) newErrors.cliente = 'El cliente es requerido';
      if (!formData.proyecto) newErrors.proyecto = 'El proyecto es requerido';
      if (!formData.presupuesto_costo) newErrors.presupuesto_costo = 'El presupuesto de costo es requerido';
      if (!formData.partida_presupuesto) newErrors.partida_presupuesto = 'La partida del presupuesto es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      console.log('🔍 Items seleccionados antes de filtrar:', itemsPresupuestoSeleccionados);

      // Filtrar items válidos
      const itemsPresupuestoValidos = itemsPresupuestoSeleccionados.filter(item => {
        // Validar que tenga item_presupuesto
        if (!item.item_presupuesto) return false;

        // Validar que cantidad_ejecutada sea un número válido > 0
        const cantidad = parseFloat(item.cantidad_ejecutada);
        return !isNaN(cantidad) && cantidad > 0;
      }).map(item => ({
        item_presupuesto: parseInt(item.item_presupuesto),
        cantidad_ejecutada: parseFloat(item.cantidad_ejecutada)
      }));

      // Preparar datos del formulario
      const submitData = { ...formData };

      // Si hay items en el nuevo sistema, usarlos y eliminar campos legacy
      if (itemsPresupuestoValidos.length > 0) {
        submitData.items_presupuesto = itemsPresupuestoValidos;
        // Eliminar campos legacy para evitar conflictos
        delete submitData.item_presupuesto;
        delete submitData.cantidad_ejecutada;
      }
      // Si no hay items pero hay datos en los campos legacy, mantenerlos
      // (esto permite usar el sistema viejo si no se agregaron items con el botón "+ Item")

      console.log('📤 Datos a enviar:', submitData);
      console.log('📋 Items presupuesto:', submitData.items_presupuesto);

      const response = await contentService.createPost(submitData);
      const createdPost = response.data;

      // Subir archivos adjuntos si los hay
      if (pendingAttachments.length > 0) {
        for (const file of pendingAttachments) {
          try {
            await contentService.uploadInfoAttachment(createdPost.id, file);
          } catch (err) {
            console.error('Error subiendo adjunto:', err);
          }
        }
      }

      alert('✅ Post creado exitosamente');

      // Notificar al padre con el ID del post creado para actualizar el calendario
      if (onUpdate) {
        onUpdate(createdPost.id);
      }

      onClose();
    } catch (error) {
      console.error('❌ Error creating post:', error);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);

      // Mostrar error detallado
      if (error.response?.data) {
        console.error('❌ Errores de validación:', JSON.stringify(error.response.data, null, 2));
      }

      alert(error.response?.data?.detail || 'Error al crear el post');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 🆕 Handler para el checkbox de presupuesto
  const handlePresupuestoToggle = (e) => {
    const isChecked = e.target.checked;
    setPresupuestoEnabled(isChecked);

    // Si se deshabilita, limpiar los campos de presupuesto
    if (!isChecked) {
      setFormData(prev => ({
        ...prev,
        cliente: '',
        proyecto: '',
        presupuesto_costo: '',
        partida_presupuesto: '',
        item_presupuesto: '',
        cantidad_ejecutada: '1'
      }));

      // Limpiar errores de validación de campos de presupuesto
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.cliente;
        delete newErrors.proyecto;
        delete newErrors.presupuesto_costo;
        delete newErrors.partida_presupuesto;
        return newErrors;
      });
    }
  };

  // 🆕 Handlers para items de presupuesto múltiples
  const addItemPresupuesto = () => {
    setItemsPresupuestoSeleccionados([
      ...itemsPresupuestoSeleccionados,
      { item_presupuesto: '', cantidad_ejecutada: '' }
    ]);
  };

  const removeItemPresupuesto = (index) => {
    const newItems = itemsPresupuestoSeleccionados.filter((_, i) => i !== index);
    setItemsPresupuestoSeleccionados(newItems);
  };

  const handleItemPresupuestoChange = (index, field, value) => {
    const newItems = [...itemsPresupuestoSeleccionados];
    newItems[index][field] = value;
    setItemsPresupuestoSeleccionados(newItems);
  };

  const calculateProgress = () => {
    let requiredFields = ['title', 'platform', 'material_type', 'influencer',
                           'publication_date', 'validation_date', 'brief'];

    // 🆕 Agregar campos de presupuesto si el checkbox está activo
    if (presupuestoEnabled) {
      requiredFields = [...requiredFields, 'cliente', 'proyecto', 'presupuesto_costo', 'partida_presupuesto'];
    }

    const filledFields = requiredFields.filter(field => {
      const value = formData[field];
      return value && value.toString().trim() !== '';
    });
    return Math.round((filledFields.length / requiredFields.length) * 100);
  };

  const progress = calculateProgress();

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header degradado verde — igual que PostDetailModal */}
        <div className="pm-header">
          <div className="pm-header-inner">
            <h2 className="pm-header-title">Crear Post</h2>
          </div>
          <button onClick={onClose} className="pm-close-btn" type="button"><i className="bi bi-x-lg" /></button>
        </div>

        <form onSubmit={handleSubmit} className="pm-form">

          {/* ── Sección: Información Básica ── */}
          <div className="pm-section">
            <p className="pm-section-label">Información Básica</p>
            <div className="form-grid form-grid--6">
                {/* Título */}
                <div className="form-group full-width">
                  <label>Título del post:</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Título del post"
                    className={errors.title ? 'error' : ''}
                  />
                  {errors.title && <span className="error-message">{errors.title}</span>}
                </div>

                {/* Etiqueta */}
                <div className="form-group span-3">
                  <label>Etiqueta:</label>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                    {/* Select de etiquetas existentes */}
                    <select
                      value={etiquetaSelectValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEtiquetaSelectValue(val);
                        if (val !== '__nueva__') {
                          // Autocompletar con el color ya usado para esta etiqueta (si existe);
                          // sin esto, cada post nuevo con una etiqueta existente caía al azul
                          // por defecto en vez de respetar el color con el que se creó esa etiqueta.
                          setFormData(prev => ({
                            ...prev,
                            etiqueta: val,
                            etiqueta_color: val ? (etiquetasColores[val] || '#3B82F6') : prev.etiqueta_color
                          }));
                        } else {
                          setFormData(prev => ({ ...prev, etiqueta: '', etiqueta_color: '#3B82F6' }));
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '9px 12px',
                        border: '1.5px solid rgba(0,36,33,0.18)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: 'white',
                        color: '#002421',
                        cursor: 'pointer',
                        appearance: 'auto',
                        fontFamily: "'Gotham', 'Helvetica Neue', Arial, sans-serif"
                      }}
                    >
                      <option value="">Sin etiqueta</option>
                      {etiquetasDisponibles.map(et => (
                        <option key={et} value={et}>{et}</option>
                      ))}
                    </select>

                    {/* El color viene del maestro: Configuración → Sistema → Etiquetas */}
                    {/* Preview del badge */}
                    {formData.etiqueta && (
                      <span style={{
                        padding: '3px 12px',
                        borderRadius: '9999px',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        backgroundColor: formData.etiqueta_color || '#3B82F6',
                        color: '#fff',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                      }}>
                        {formData.etiqueta}
                      </span>
                    )}
                  </div>
                </div>

                {/* Desarrolla */}
                <div className="form-group span-3">
                  <label>Desarrolla:</label>
                  <select
                    value={formData.desarrolla}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, desarrolla: e.target.value }));
                      if (errors.desarrolla) setErrors(prev => ({ ...prev, desarrolla: '' }));
                    }}
                    className={errors.desarrolla ? 'error' : ''}
                  >
                    <option value="">Seleccionar...</option>
                    {Array.isArray(desarrollaOptions) && desarrollaOptions.map(d => (
                      <option key={d.id} value={d.id.toString()}>{d.description || d.name}</option>
                    ))}
                  </select>
                  {errors.desarrolla && <span className="error-message">{errors.desarrolla}</span>}
                </div>

                {/* Plataforma + Tipo de Material + Cuenta Publicación — en una sola fila de 3 */}
                <div className="form-group span-3">
                  <label>Plataforma:</label>
                  <select
                    value={formData.platform}
                    onChange={(e) => {
                      const nuevaPlataforma = e.target.value;
                      const nombre = platforms.find(p => String(p.id) === nuevaPlataforma)?.name;
                      setFormData(prev => ({
                        ...prev,
                        platform: nuevaPlataforma,
                        // si el tipo de material elegido no existe en la nueva plataforma, se limpia
                        material_type: materialPermitido(nombre, prev.material_type) ? prev.material_type : ''
                      }));
                      if (errors.platform) setErrors(prev => ({ ...prev, platform: '' }));
                    }}
                    className={errors.platform ? 'error' : ''}
                  >
                    <option value="">Seleccionar...</option>
                    {Array.isArray(platforms) && platforms.map(p => (
                      <option key={p.id} value={p.id.toString()}>{p.name}</option>
                    ))}
                  </select>
                  {errors.platform && <span className="error-message">{errors.platform}</span>}
                </div>

                <div className="form-group span-3">
                  <label>Tipo de Material:</label>
                  <select
                    name="material_type"
                    value={formData.material_type}
                    onChange={handleChange}
                    className={errors.material_type ? 'error' : ''}
                    disabled={!formData.platform}
                  >
                    <option value="">{formData.platform ? 'Seleccionar...' : 'Elige una plataforma primero'}</option>
                    {materialesParaPlataforma(
                      platforms.find(p => String(p.id) === String(formData.platform))?.name,
                      formData.material_type
                    ).map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {errors.material_type && <span className="error-message">{errors.material_type}</span>}
                </div>

                <div className="form-group span-3">
                  <label>Cuenta Publicación:</label>
                  <select
                    name="influencer"
                    value={formData.influencer}
                    onChange={handleChange}
                    className={errors.influencer ? 'error' : ''}
                  >
                    <option value="">Seleccionar cuenta...</option>
                    {Array.isArray(influencers) && influencers.length > 0 ? (
                      influencers.map(i => (
                        <option key={i.id} value={i.id}>
                          {i.name} (@{i.username})
                        </option>
                      ))
                    ) : (
                      <option disabled>No hay influencers disponibles</option>
                    )}
                  </select>
                  {errors.influencer && <span className="error-message">{errors.influencer}</span>}
                </div>
              </div>
          </div>

          {/* ── Sección: Programación ── */}
          <div className="pm-section">
            <p className="pm-section-label">Programación</p>
              <div className="form-grid">
                {/* Fecha de Publicación */}
                <div className="form-group">
                  <label>Fecha de Publicación:</label>
                  <input
                    type="date"
                    name="publication_date"
                    value={formData.publication_date}
                    onChange={handleChange}
                    onClick={abrirCalendario}
                    style={{ cursor: 'pointer' }}
                    className={errors.publication_date ? 'error' : ''}
                  />
                  {errors.publication_date && <span className="error-message">{errors.publication_date}</span>}
                </div>

                {/* Fecha de Validación */}
                <div className="form-group">
                  <label>Fecha de Validación:</label>
                  <input
                    type="date"
                    name="validation_date"
                    value={formData.validation_date}
                    onChange={handleChange}
                    onClick={abrirCalendario}
                    style={{ cursor: 'pointer' }}
                    className={errors.validation_date ? 'error' : ''}
                  />
                  {errors.validation_date && <span className="error-message">{errors.validation_date}</span>}
                </div>
              </div>
          </div>

          {/* ── Sección: Contenido del Brief ── */}
          <div className="pm-section">
            <p className="pm-section-label">Contenido del Brief</p>
              <div className="form-content-grid">
                {/* Brief Principal */}
                <div className="form-group full-width">
                  <label>Brief:</label>
                  <RichTextEditor
                    initialContent={formData.brief}
                    onUpdate={({ html }) => {
                      setFormData(prev => ({ ...prev, brief: html }));
                      if (errors.brief) setErrors(prev => ({ ...prev, brief: '' }));
                    }}
                    placeholder="Describe la idea principal del contenido, el concepto creativo y los requisitos específicos..."
                  />
                  {errors.brief && <span className="error-message">{errors.brief}</span>}
                </div>

                {/* Archivos adjuntos del brief */}
                <div className="form-group full-width">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {pendingAttachments.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.4rem' }}>
                        {pendingAttachments.map((file, idx) => (
                          <div key={idx} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '0.4rem 0.75rem', background: '#f8fafc',
                            border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8rem'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                              <i className="bi bi-file-earmark" style={{ color: '#6b7280', flexShrink: 0 }} />
                              {file.name} <span style={{ color: '#9ca3af' }}>({(file.size / 1024).toFixed(1)} KB)</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', marginLeft: '0.5rem', fontSize: '0.9rem', display: 'flex', alignItems: 'center' }}
                            ><i className="bi bi-x-lg" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label className="pm-attach-btn" title="Adjuntar archivos">
                      <input
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const newFiles = Array.from(e.target.files);
                          setPendingAttachments(prev => [...prev, ...newFiles]);
                          e.target.value = '';
                        }}
                      />
                      <i className="bi bi-paperclip" />
                      <span>Adjuntar archivo</span>
                    </label>
                  </div>
                </div>

                {/* Objetivos, Mensajes Clave y Audiencia — ocultos */}
              </div>
          </div>

        </form>

        {/* Footer */}
        <div className="pm-footer">
          <button type="button" onClick={onClose} className="pm-btn-cancel">
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="pm-btn-create"
            disabled={loading}
          >
            {loading ? 'Creando...' : 'Crear Post'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default PostModal;